import { useState } from 'react';
import { Check, X, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  useSessionRates,
  useUpsertSessionRate,
  useRemoveSessionRate,
  type SessionType,
  type SessionRate,
} from '@/hooks/use-session-rates';
import { formatCurrency } from '@/lib/format';

const SESSION_TYPES: Array<{ value: SessionType; label: string }> = [
  { value: 'group',         label: 'Group' },
  { value: 'private',       label: 'Private' },
  { value: 'clinic',        label: 'Clinic' },
  { value: 'match_play',    label: 'Match Play' },
  { value: 'cardio_tennis', label: 'Cardio Tennis' },
  { value: 'squads',        label: 'Squads' },
];

export function SessionRatesSettings() {
  const { data: rates, isLoading } = useSessionRates();
  const upsert = useUpsertSessionRate();
  const removeRate = useRemoveSessionRate();

  const [editingType, setEditingType] = useState<SessionType | null>(null);
  const [editValue, setEditValue] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);

  const rateByType = Object.fromEntries(
    (rates ?? []).map((r: SessionRate) => [r.sessionType, r])
  );

  const handleEditClick = (type: SessionType) => {
    const existing = rateByType[type];
    setEditingType(type);
    setEditValue(
      existing?.termRateCents != null
        ? String(existing.termRateCents / 100)
        : ''
    );
    setSaveError(null);
  };

  const handleCancel = () => {
    setEditingType(null);
    setSaveError(null);
  };

  const handleSave = async (type: SessionType) => {
    const raw = editValue.trim();

    if (!raw) {
      // Blank → remove the rate
      await removeRate.mutateAsync(type);
      setEditingType(null);
      return;
    }

    const cents = Math.round(parseFloat(raw) * 100);
    if (isNaN(cents) || cents < 0) {
      setSaveError('Enter a valid non-negative amount.');
      return;
    }

    setSaveError(null);
    try {
      await upsert.mutateAsync({ sessionType: type, termRateCents: cents });
      setEditingType(null);
    } catch {
      setSaveError('Failed to save. Please try again.');
    }
  };

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading rates...</p>;
  }

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 pr-6 font-medium text-gray-600">Session Type</th>
              <th className="text-left py-2 pr-6 font-medium text-gray-600">Term Rate</th>
              <th className="w-16" />
            </tr>
          </thead>
          <tbody>
            {SESSION_TYPES.map(({ value, label }) => {
              const existing = rateByType[value];
              const isEditing = editingType === value;

              return (
                <tr key={value} className="border-b last:border-0">
                  <td className="py-2.5 pr-6 font-medium text-gray-800">{label}</td>

                  {isEditing ? (
                    <>
                      <td className="py-2 pr-4">
                        <div className="relative">
                          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">$</span>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            placeholder="0.00"
                            className="h-8 pl-6 w-32 text-sm"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSave(value);
                              if (e.key === 'Escape') handleCancel();
                            }}
                          />
                        </div>
                        {saveError && (
                          <p className="text-xs text-red-500 mt-1">{saveError}</p>
                        )}
                      </td>
                      <td className="py-2">
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleSave(value)}
                            disabled={upsert.isPending || removeRate.isPending}
                            className="h-7 w-7 p-0"
                          >
                            <Check className="w-4 h-4 text-emerald-600" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={handleCancel}
                            className="h-7 w-7 p-0"
                          >
                            <X className="w-4 h-4 text-gray-400" />
                          </Button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="py-2.5 pr-6 text-gray-700">
                        {existing?.termRateCents != null
                          ? formatCurrency(existing.termRateCents)
                          : <span className="text-gray-400">Not set</span>}
                      </td>
                      <td className="py-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEditClick(value)}
                          disabled={!!editingType}
                          className="h-7 w-7 p-0"
                        >
                          <Pencil className="w-3.5 h-3.5 text-gray-400" />
                        </Button>
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">
        Leave blank when editing to remove a rate. Rates are applied per enrollment when
        generating term invoices.
      </p>
    </div>
  );
}
